import React from 'react';
import IconBox from '../../../shared/components/IconBox';
import PercentageBox from '../../../shared/components/PercentageBox';
import TrafficButtons from '../../../shared/components/TrafficButtons';
import Shield from '../../../shared/components/Shield';
import { Pill, fraudPillColors } from '../../../shared/components/tableUtils';

import { ReactComponent as ClickIcon } from '../../../assets/icons/click.svg';
import { ReactComponent as HumanIcon } from '../../../assets/icons/human.svg';
import { ReactComponent as BotIcon } from '../../../assets/icons/bot.svg';

const numberFormatter = new Intl.NumberFormat('en-US');

const sumTraffic = (data) =>
  data.reduce(
    ([humanTotal, botTotal], day) =>
      Object.values(day).reduce(
        ([humanSum, botSum], [human, bot]) => [humanSum + human, botSum + bot],
        [humanTotal, botTotal]
      ),
    [0, 0]
  );

const formatFraudLevelLabel = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return 'No traffic';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
};

const SiteSummaryCard = ({site}) => {
  const [statusLevel, setStatusLevel] = React.useState(site?.detectionLevel);
  const [humanTraffic, botTraffic] = sumTraffic(site.data);
  const totalTraffic = botTraffic + humanTraffic;
  const botClickPercentage = totalTraffic === 0 ? 0 : (botTraffic / totalTraffic) * 100;
  const status = site?.status || null;
  const fraudLevel = site?.fraudLevel || null;
  const hasFraudLevel = Boolean(fraudLevel);
  const showFraudLevel = hasFraudLevel && status !== 'red';
  const [oldHumanTraffic, oldBotTraffic] = site.oldCounts || [0, 0];
  const oldTotalTraffic = oldHumanTraffic + oldBotTraffic;
  const oldBotPercentage = oldTotalTraffic === 0 ? 0 : oldBotTraffic / oldTotalTraffic;
  const botPercentage = totalTraffic === 0 ? 0 : botTraffic / totalTraffic;
  let botPercentageDifference;

  if (oldTotalTraffic === 0) {
    botPercentageDifference = null;
  }
  else if (totalTraffic === 0) {
    botPercentageDifference = -100;
  }
  else {
    botPercentageDifference = (botPercentage - oldBotPercentage) * 100;
  }
  const fraudLevelLabel = formatFraudLevelLabel(fraudLevel);
  const { bg: fraudBg, fg: fraudFg } = fraudPillColors(fraudLevel);
  React.useEffect(() => {
    setStatusLevel(site?.detectionLevel);
  }, [site?.detectionLevel]);

  const statusLabel = ({
    disabled: 'Disabled',
    detection_only: 'Monitoring',
    true_traffic: 'Active',
  })[statusLevel] || 'Active';

  return (
    <div className="overview-summary">
      <div className="overview-summary__cover gradient">
        <div className="overview-summary__header">
          <div className="overview-summary__identity">
            <IconBox website={site.website} className="overview-summary__favicon" />
            <h3 className="overview-summary__name" title={site.website}>
              {site.website}
            </h3>
          </div>
        </div>

        <div className="overview-summary__divider" aria-hidden="true" />

        <div className="overview-summary__details">
          <div className="overview-summary__detail overview-summary__detail--fraud">
            <p className="overview-summary__detail-label">Fraud Level</p>
            <div className="overview-summary__detail-body overview-summary__detail-body--fraud">
              <div className="overview-summary__fraud-row">
                {showFraudLevel ? (
                  <>
                    <Shield className="overview-summary__shield" fraudLevel={fraudLevel} />
                    <Pill text={fraudLevelLabel} bg={fraudBg} fg={fraudFg} />
                  </>
                ) : status === 'red' ? (
                  <span className="metric-value-secondary">Waiting for traffic...</span>
                ) : (
                  <span className="metric-value-secondary">Unavailable</span>
                )}
              </div>
            </div>
          </div>
          <div className="overview-summary__detail overview-summary__detail--status">
            <p className="overview-summary__detail-label">Status</p>
            <div className="overview-summary__detail-body overview-summary__detail-body--status">
              <div className="overview-summary__status-stack">
                <TrafficButtons
                  direction='row'
                  site={site}
                  showLabel={false}
                  onSelectionChange={setStatusLevel}
                />
                <span className="overview-summary__status-text">
                  {statusLabel}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="overview-summary__traffic gradient">
        <div className="overview-summary__metric-row">
          <div className="overview-summary__metric-main">
            <p className="overview-metric__label">Total Traffic</p>
            <div className="overview-summary__metric-value-row">
              <ClickIcon className="icon base icon-click overview-metric__icon" />
              <div className="overview-summary__metric-copy">
                <span className="metric-value-primary">{numberFormatter.format(totalTraffic)}</span>
              </div>
            </div>
          </div>
          <div className="overview-summary__metric-aside is-empty" aria-hidden="true" />
        </div>

        <div className="overview-summary__metric-row">
          <div className="overview-summary__metric-main">
            <p className="overview-metric__label">Human Traffic</p>
            <div className="overview-summary__metric-value-row">
              <HumanIcon className="icon base icon-human overview-metric__icon" />
              <div className="overview-summary__metric-copy">
                <span className="metric-value-primary">{numberFormatter.format(humanTraffic)}</span>
              </div>
            </div>
          </div>
          <div className="overview-summary__metric-aside is-empty" aria-hidden="true" />
        </div>

        <div className="overview-summary__metric-row overview-summary__metric-row--bot">
          <div className="overview-summary__metric-main">
            <p className="overview-metric__label">Bot Traffic</p>
            <div className="overview-summary__metric-value-row">
              <BotIcon className="icon base icon-bot overview-metric__icon" />
              <div className="overview-summary__metric-copy overview-summary__metric-copy--bot">
                <span className="metric-value-primary">{numberFormatter.format(botTraffic)}</span>
                <span className="metric-value-separator" aria-hidden="true">|</span>
                <span className="metric-value-secondary overview-metric__meta--bot">
                  {botClickPercentage.toFixed(2)}%
                </span>
              </div>
            </div>
          </div>
          <div className="overview-summary__metric-aside">
            <PercentageBox percentage={botPercentageDifference} bot={true} />
            <small className="overview-metric__trend-note">vs previous period</small>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SiteSummaryCard;
